<template>
  <div class="cost-page">
    <PageWrapperComponent v-if="!loadingShop">
      <BoxComponent v-if="showVisibilityWarning && showVisibilityWarningNotification" :mt="48" :mb="48">
        <FlexComponent :gap="16" class="notification" align="center">
          <button class="notification__close" @click="showVisibilityWarningNotification = false">
            <CloseIcon />
          </button>
          <InfoIcon class="notification__info-icon" />
          Your store isn't visible to all creators. If you like to make it visible to everyone, you can adjust this in your 'store settings'
          anytime.
          <router-link :to="{ name: 'edit-advertiser' }" class="notification__link">
            <ButtonComponent variant="accent"> Go to store settings </ButtonComponent>
          </router-link>
        </FlexComponent>
      </BoxComponent>

      <DialogModal v-model="showVisibilityWarningPopup" :width="540">
        <template #header>
          <TypographyComponent variant="headline-medium"> Visibility </TypographyComponent>
        </template>
        <TypographyComponent variant="body-large">
          Your store isn't visible to all creators. Would you like to make it visible to everyone?
        </TypographyComponent>
        <BoxComponent :mb="24" :mt="16">
          <FlexComponent :gap="16" class="notification" align="center">
            <InfoIcon class="info-icon" />
            <TypographyComponent variant="body-large" class="note">
              You can adjust this in your 'store settings' anytime.
            </TypographyComponent>
          </FlexComponent>
        </BoxComponent>
        <FlexComponent :gap="8" justify="flex-end">
          <button class="notification__link-button" @click="showVisibilityWarningPopup = false">Keep my settings</button>
          <ButtonComponent
            variant="accent"
            @click="
              () => {
                updateHidden(0)
                sendForm()
                showVisibilityWarningPopup = false
              }
            "
          >
            Show to all creators
          </ButtonComponent>
        </FlexComponent>
      </DialogModal>

      <BoxComponent :mt="48">
        <BoxComponent :mb="24">
          <TypographyComponent id="costs-pricing-section" variant="headline-medium">Default rates</TypographyComponent>
        </BoxComponent>
        <div class="default-rate-container">
          <CostCard
            title="Default payment rates"
            :is-base-card="true"
            :enable-cpa="shop.advertiserUsesCpa"
            :traffic-sources="shopTrafficSources"
            @save-new-costs="saveBaseCosts"
          />
        </div>
      </BoxComponent>
    </PageWrapperComponent>
  </div>
</template>

<script lang="ts">
import { DialogModal, PageWrapperComponent } from '@metapic-apps/shared'
import { ElNotification } from 'element-plus'
import { mapActions, mapGetters } from 'vuex'

import InfoIcon from '@/assets/icons/info.svg?component'
import CloseIcon from '@/assets/icons/remove-small.svg?component'
import CostCard from '@/components/costs/CostCard.vue'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

export default {
  name: 'Costs',
  components: {
    BoxComponent,
    TypographyComponent,
    DialogModal,
    FlexComponent,
    PageWrapperComponent,
    CostCard,
    InfoIcon,
    CloseIcon,
  },
  middleware: 'auth',
  data: function () {
    return {
      allTagsID: 1,
      showVisibilityWarningPopup: false,
      showVisibilityWarningNotification: true,
    }
  },
  computed: {
    ...mapGetters('shop', {
      shop: 'shop',
      loadingShop: 'loading',
      savingShopDetails: 'savingShopDetails',
      shopTrafficSources: 'shopTrafficSources',
      storeVisibility: 'hidden',
    }),
    showVisibilityWarning() {
      return this.haveCosts && this.storeVisibility !== 0
    },
    haveCosts() {
      let costs = false
      this.shopTrafficSources?.forEach((el) => {
        if (el.cpc || el.cpa) {
          costs = true
        }
      })
      return costs
    },
  },
  watch: {
    savingShopDetails: function (newValue, oldValue) {
      if (!newValue && oldValue) {
        this.notifySuccess()
      }
    },
  },
  created() {
    this.$store.dispatch('trafficSources/fetchTrafficSources')
  },
  methods: {
    ...mapActions('shop', ['saveCosts', 'updateHidden', 'sendForm']),
    async saveBaseCosts(newBaseCosts) {
      if (!this.haveCosts) {
        this.showVisibilityWarningPopup = true
      }
      await this.saveCosts(newBaseCosts)
      this.allTagsID++
    },
    notifySuccess() {
      ElNotification({
        title: 'Success!',
        message: 'Your changes have been saved',
        type: 'success',
      })
    },
  },
}
</script>

<style scoped lang="scss">
.cost-page {
  .default-rate-container {
    display: flex;
    flex-wrap: wrap;

    > * {
      margin-right: 16px;
      @media (max-width: 700px) {
        margin-right: 0;
        margin-top: 16px;
      }
    }
  }

  .all-tags-edit-container {
    display: grid;
    grid-template-columns: 654px;
    @media (max-width: 768px) {
      grid-template-columns: 320px;
    }
  }

  .costs-popup-tag-wrapper-1 {
    position: absolute;
    z-index: 100;
    margin-left: 400px;
  }

  .all-tags-edit-container {
    gap: 16px;
    margin-bottom: 16px;
    > * {
      @media (max-width: 500px) {
        margin: 0 auto;
      }
    }

    .costs-popup-tag-wrapper-2 {
      position: absolute;
      z-index: 100;
      margin-left: 350px;
      margin-top: -150px;
    }
  }
}

.notification {
  position: relative;
  padding: 16px 40px 16px 16px;
  border-radius: 16px;
  background-color: #f3f7f9;
}

.notification__close {
  position: absolute;
  top: -4px;
  right: -4px;

  width: 34px;
  height: 34px;

  border: none;
  border-radius: 34px;
  background: #f3f7f9;
  box-shadow: 0 2px 8px 0 rgba(60, 75, 93, 0.16);
  cursor: pointer;
}

.notification__link {
  margin-left: auto;
}

.notification__link-button {
  border: none;
  background: none;
  font-weight: 500;
  color: #000000;
}

.notification__info-icon {
  flex: 0 0 24px;
}

.info-icon {
  flex: 0 0 24px;
}
</style>
